import React from "react";
import MuiCard from "@mui/material/Card";
import PropTypes from "prop-types";

/**
 * Card component wrapper for card containers.
 * Wraps Material-UI Card with theme integration and automatic responsive width.
 * 
 * This component automatically applies responsive width constraints to prevent
 * overflow on small screens and at high zoom levels (WCAG 2.1 Level AA compliance).
 * 
 * Smart Defaults:
 * - Automatically applies maxWidth: "100%" to prevent overflow
 * - Automatically applies wordWrap: "break-word" to handle long text
 * - Respects any sx prop provided by user (merged with defaults)
 * - Maintains desktop layout while ensuring mobile/zoom accessibility
 * 
 * The solution is applied purely through sx props, without modifying CSS files.
 * This provides a global fix for all Card components throughout the application.
 * 
 * @component
 * @example
 * <Card>
 *   <CardContent>
 *     <Typography>Card content</Typography>
 *   </CardContent>
 * </Card>
 * 
 * @example
 * // With custom styling - maxWidth: "100%" is still automatically added
 * <Card sx={{ width: "600px", backgroundColor: "blue" }}>
 *   <CardContent>Content</CardContent>
 * </Card>
 * 
 * @example
 * // Using className - sx solution still applies
 * <Card className="messageCenter__card__container">
 *   <CardContent>Content</CardContent>
 * </Card>
 */
const Card = React.forwardRef(({ children, sx, ...props }, ref) => {
  // Apply responsive defaults to prevent overflow
  // These styles ensure WCAG 2.1 Level AA compliance (400% zoom requirement)
  const baseSx = {
    maxWidth: "100%",        // Always prevent horizontal overflow
    wordWrap: "break-word",  // Break long words to prevent overflow
  };

  // Merge with user-provided sx (user sx takes precedence for overrides)
  const mergedSx = sx 
    ? Array.isArray(sx) 
      ? [baseSx, ...sx]      // If sx is an array, prepend baseSx
      : { ...baseSx, ...sx } // If sx is an object, merge with baseSx first
    : baseSx;

  return <MuiCard ref={ref} sx={mergedSx} {...props}>{children}</MuiCard>;
});

Card.displayName = 'Card';

Card.propTypes = {
  /**
   * The content of the component
   */
  children: PropTypes.node,
  /**
   * If true, the card will use raised styling
   */
  raised: PropTypes.bool,
  /**
   * The system prop that allows defining system overrides as well as additional CSS styles
   */
  sx: PropTypes.oneOfType([
    PropTypes.arrayOf(
      PropTypes.oneOfType([PropTypes.func, PropTypes.object, PropTypes.bool])
    ),
    PropTypes.func,
    PropTypes.object,
  ]),
};

// No defaultProps - maintain exact MUI Card behavior
// Card extends Paper component

export default Card;

